// 현장 계획 일정 및 메모 모델. 백엔드 entity.SiteSchedule 와 1:1 매칭.
import type { CreatedByFields } from './CreatedBy';

// 7종 — 출장 / 점검 / 장애 / 미팅 / 업무 일지 / 메모 / 기타.
// 기존 deadline / inspection 은 실제 운영에서 거의 사용되지 않아 제거하고
// incident / meeting 으로 대체. legacy DB 행에 deadline/inspection 가 남아
// 있으면 UI 에서 '기타' 로 fallback 표시 (eventTypeFallback 헬퍼).
//
// memo 는 다른 유형과 성질이 다르다 — 시점(scheduled_at)이 없어도 되는 유일한
// 유형이다. 자세한 이유는 MEMO_EVENT_TYPE / scheduled_at 주석 참고.
export type ScheduleEventType =
  | 'visit'
  | 'maintenance'
  | 'incident'
  | 'meeting'
  | 'worklog'
  | 'memo'
  | 'other';
export type ScheduleStatus = 'planned' | 'in_progress' | 'done' | 'cancelled';

export const SCHEDULE_EVENT_TYPES: ScheduleEventType[] = [
  'visit',
  'maintenance',
  'incident',
  'meeting',
  'worklog',
  'memo',
  'other',
];
export const SCHEDULE_STATUSES: ScheduleStatus[] = ['planned', 'in_progress', 'done', 'cancelled'];

export const EVENT_TYPE_LABEL: Record<ScheduleEventType, string> = {
  visit: '출장',
  maintenance: '점검',
  incident: '장애',
  meeting: '미팅',
  worklog: '업무 일지',
  memo: '공지사항',
  other: '기타',
};

/**
 * 시점 없이 남길 수 있는 유일한 유형. 메모는 "언제인지 모르지만 남겨둬야 하는"
 * 내용을 위한 것이라 날짜가 필수가 아니다 — 날짜를 채운 메모는 일정과 똑같이
 * 캘린더에 뜬다.
 */
export const MEMO_EVENT_TYPE: ScheduleEventType = 'memo';

/** 이 유형은 시작일 없이 저장할 수 있는가. 백엔드 검증과 같은 규칙. */
export const allowsMissingDate = (t: ScheduleEventType): boolean => t === MEMO_EVENT_TYPE;

/** legacy event_type 값 (deadline/inspection 등) 을 안전하게 6종 안으로 매핑. */
export function eventTypeFallback(raw: string | undefined | null): ScheduleEventType {
  if (
    raw === 'visit' ||
    raw === 'maintenance' ||
    raw === 'incident' ||
    raw === 'meeting' ||
    raw === 'worklog' ||
    raw === 'memo' ||
    raw === 'other'
  ) {
    return raw;
  }
  return 'other';
}

export const SCHEDULE_STATUS_LABEL: Record<ScheduleStatus, string> = {
  planned: '예정',
  in_progress: '진행중',
  done: '완료',
  cancelled: '취소',
};

export interface SiteSchedule extends CreatedByFields {
  id: number;
  /** 공통 메모(현장 미지정)는 null. */
  site_project_id: number | null;
  event_type: ScheduleEventType;
  status: ScheduleStatus;
  title: string;
  description?: string;
  /**
   * ISO 8601 또는 null. **null 이면 시점 없는 메모**다 (event_type='memo').
   *
   * null 을 `new Date()` 에 그대로 넣으면 Invalid Date 가 되어 정렬이 조용히
   * 망가지고 목록에 "Invalid Date" 가 찍힌다. 반드시 hasScheduleDate /
   * scheduleTime 을 거쳐 다뤄야 한다.
   */
  scheduled_at?: string | null;
  scheduled_end_at?: string | null;
  assignee?: string;
  location?: string;
  created_at: string;
  updated_at: string;
}

/** 시점이 있는 항목인가 — 캘린더·기간 필터·날짜 정렬의 전제 조건. */
export const hasScheduleDate = (s: Pick<SiteSchedule, 'scheduled_at'>): boolean =>
  !!s.scheduled_at && !Number.isNaN(new Date(s.scheduled_at).getTime());

/**
 * 정렬/비교용 epoch ms. 시점이 없거나 파싱 불가면 null —
 * NaN 을 돌려주면 비교가 항상 false 가 되어 정렬 순서가 입력 순서에 따라
 * 달라지는(구현 정의) 버그가 된다.
 */
export const scheduleTime = (s: Pick<SiteSchedule, 'scheduled_at'>): number | null => {
  if (!s.scheduled_at) return null;
  const t = new Date(s.scheduled_at).getTime();
  return Number.isNaN(t) ? null : t;
};

/** 시점 없는 메모를 뒤로 보내는 비교자 — 목록 정렬에서 공통으로 쓴다. */
export const compareByScheduleTime = (
  a: Pick<SiteSchedule, 'scheduled_at'>,
  b: Pick<SiteSchedule, 'scheduled_at'>,
  dir: 'asc' | 'desc' = 'asc',
): number => {
  const ta = scheduleTime(a);
  const tb = scheduleTime(b);
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1; // 날짜 없는 항목은 항상 뒤
  if (tb === null) return -1;
  return dir === 'asc' ? ta - tb : tb - ta;
};

/**
 * 전체 현장 통합 일정 — 캘린더용. 백엔드 GET /v0.1/site-management/schedules
 * 응답. SiteSchedule 에 현장 표시 이름(site_name)을 더해 캘린더 이벤트 라벨에 사용.
 */
export interface AllSiteSchedule extends SiteSchedule {
  /** 현장 표시 이름 — name_ko 우선, 없으면 name. 백엔드가 join 으로 채움. */
  site_name: string;
}

export interface CreateSiteScheduleRequest {
  event_type: ScheduleEventType;
  status: ScheduleStatus;
  title: string;
  description?: string;
  /** null 은 메모(event_type='memo')에서만 허용 — 백엔드가 그 외에는 400. */
  scheduled_at: string | null;
  scheduled_end_at?: string | null;
  assignee?: string;
  location?: string;
}

export interface UpdateSiteScheduleRequest {
  site_project_id?: number;
  /** 현장을 비워 공통 메모로 되돌린다 (메모에만 허용). */
  clear_site_project_id?: boolean;
  event_type?: ScheduleEventType;
  status?: ScheduleStatus;
  title?: string;
  description?: string;
  scheduled_at?: string;
  scheduled_end_at?: string | null;
  /**
   * 날짜를 **지우려는** 의도. JSON null 은 백엔드(Go)에서 nil 포인터가 되어
   * "미변경" 과 구분되지 않으므로, 지우기는 이 플래그로만 전달된다.
   * 일정을 메모로 되돌릴 때 사용.
   */
  clear_scheduled_at?: boolean;
  clear_scheduled_end_at?: boolean;
  assignee?: string;
  location?: string;
}
